<template>
  <div class="add-sdk-product">
    <v-container>
      <div class="back-link" @click="router.push({ name: 'AccountView' })">
        <img src="@/assets/images/icons/arrow-left.svg" alt="Back" class="back-icon" />
        <span>Return to Account</span>
      </div>

      <v-card>
        <v-card-title>Add Structure SDK Access</v-card-title>
        <v-card-text>
          <p class="mb-4">
            Choose an SDK subscription tier to add to your Capture Enterprise account.
          </p>

          <!-- Tier selection -->
          <div v-if="!checkoutReady">
            <v-row v-if="isLoadingTiers" justify="center" class="py-8">
              <v-progress-circular indeterminate color="primary" />
            </v-row>

            <v-row v-else>
              <v-col
                v-for="tier in sdkTiers"
                :key="tier.id"
                cols="12"
                md="4"
              >
                <v-card
                  class="tier-card"
                  :class="{ 'selected-card': selectedTier && selectedTier.id === tier.id }"
                  variant="outlined"
                  @click="selectedTier = tier"
                >
                  <v-card-title>{{ tier.name }}</v-card-title>
                  <v-card-subtitle>${{ tier.monthly_price }}/mo</v-card-subtitle>
                </v-card>
              </v-col>
            </v-row>

            <div class="mt-4 d-flex justify-center">
              <v-btn
                color="primary"
                class="str-btn-primary"
                :disabled="!selectedTier || isStartingCheckout"
                @click="startCheckout"
              >
                <v-progress-circular v-if="isStartingCheckout" indeterminate size="20" class="mr-2" />
                Continue to Payment
              </v-btn>
            </div>
          </div>

          <!-- Stripe checkout -->
          <div v-if="checkoutReady">
            <div v-if="!stripeLoaded" class="text-center py-8">
              <v-progress-circular indeterminate color="primary" />
              <p class="mt-4 text-medium-emphasis">Loading Secure Payment Form...</p>
            </div>
            <div id="add-sdk-checkout"></div>
          </div>

          <v-alert v-if="errorMessage" type="error" class="mt-4">
            {{ errorMessage }}
            <v-btn
              v-if="sdkTiers.length === 0 && !checkoutReady"
              class="mt-2 ml-2"
              size="small"
              color="white"
              variant="text"
              @click="fetchTiers"
            >
              Retry
            </v-btn>
          </v-alert>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAuth0 } from '@auth0/auth0-vue';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';

const router = useRouter();
const store = useStore();
const { getAccessTokenSilently } = useAuth0();

const customerType = computed(() => store.state.authentication.customer_type);

const sdkTiers = ref([]);
const selectedTier = ref(null);
const isLoadingTiers = ref(true);
const isStartingCheckout = ref(false);
const checkoutReady = ref(false);
const stripeLoaded = ref(false);
const errorMessage = ref(null);
const checkoutRef = ref(null);

const publishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY;
const BASE_URL = process.env.VUE_APP_CBE_BASE_URL;

const SDK_TIER_EXCLUSIONS = new Set(['Enterprise', 'Structure Capture Enterprise']);

async function fetchTiers() {
  if (customerType.value === 'sdk' || customerType.value === 'both') {
    errorMessage.value = 'You already have SDK access. Please contact support to manage your subscription.';
    return;
  }

  isLoadingTiers.value = true;
  errorMessage.value = null;

  try {
    const token = await getAccessTokenSilently();
    const { data } = await axios.get(`${BASE_URL}/piccolo/admin/tiers`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    sdkTiers.value = data.filter(t => !SDK_TIER_EXCLUSIONS.has(t.name));
  } catch (err) {
    errorMessage.value = err.response?.data?.detail || err.message || 'Failed to load subscription tiers. Please try again.';
  } finally {
    isLoadingTiers.value = false;
  }
}

onMounted(fetchTiers);

async function startCheckout() {
  if (!selectedTier.value) return;

  isStartingCheckout.value = true;
  errorMessage.value = null;

  try {
    const token = await getAccessTokenSilently();
    const headers = { Authorization: `Bearer ${token}` };

    const stripe = await loadStripe(publishableKey);
    if (!stripe) {
      errorMessage.value = 'Failed to load the payment form. Please check your internet connection or disable ad-blockers and try again.';
      return;
    }

    const payload = { price_id: selectedTier.value.stripe_price_id };
    if (selectedTier.value.stripe_metered_price_id) {
      payload.metered_price_id = selectedTier.value.stripe_metered_price_id;
    }

    const { data } = await axios.post(
      `${BASE_URL}/stripe/create-subscription`,
      payload,
      { headers }
    );

    if (!data.clientSecret) {
      errorMessage.value = 'Failed to initialize checkout. Please try again.';
      return;
    }

    checkoutReady.value = true;

    await new Promise(resolve => setTimeout(resolve, 50)); // let DOM render the #add-sdk-checkout div
    const checkout = await stripe.initEmbeddedCheckout({ clientSecret: data.clientSecret });
    checkoutRef.value = checkout;
    checkout.mount('#add-sdk-checkout');
    stripeLoaded.value = true;
  } catch (err) {
    checkoutReady.value = false;
    errorMessage.value = err.response?.data?.error || 'Failed to load payment form. Please try again.';
  } finally {
    isStartingCheckout.value = false;
  }
}

onUnmounted(() => {
  if (checkoutRef.value) {
    checkoutRef.value.destroy();
    checkoutRef.value = null;
  }
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/AddSdkProduct.scss'
</style>
